// Import necessary dependencies and utility functions
import { Link } from 'react-router-dom';
import { Building2, MapPin, Briefcase, Euro } from 'lucide-react';
import { formatDate, formatSalary } from '../../lib/utils'; // Utility functions for formatting dates and salaries
import type { Job } from '../../types/job'; // Job type definition

// Define the props for the JobList component
interface JobListProps {
  jobs: Job[]; // Array of job objects to display
  onSelectJob?: (jobId: number) => void; // Callback for selecting a job
  selectedJobId?: number | null; // ID of the currently selected job
}

// Functional component to render the list of jobs
export function JobList({ jobs, onSelectJob, selectedJobId }: JobListProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {/* Map over the jobs array and render each job as a card */}
      {jobs.map((job) => (
        <div
          key={job.id} // Unique key for React's rendering
          onClick={() => onSelectJob?.(job.id)} // Trigger onSelectJob callback when clicked
          className={`card cursor-pointer transition-all ${
            selectedJobId === job.id ? 'ring-2 ring-primary-main' : '' // Highlight selected job
          }`}
        >
          <div className="p-6">
            {/* Job Title */}
            <h2 className="text-xl font-semibold text-primary-dark mb-4">
              {job.job_title}
            </h2>

            {/* Job Details */}
            <div className="space-y-3 text-text-secondary">
              {/* Company Information */}
              <div className="flex items-center">
                <Building2 className="feature-icon" />
                <div className="flex flex-col">
                  <span className="font-medium">{job.business}</span>
                  {job.business_sector && (
                    <span className="text-sm text-text-light">
                      {job.business_sector}
                    </span>
                  )}
                </div>
              </div>

              {/* Location Information */}
              <div className="flex items-center">
                <MapPin className="feature-icon" />
                <div className="flex flex-col">
                  <span>{job.city} ({job.postal_code})</span>
                  {job.state && (
                    <span className="text-sm text-text-light">{job.state}</span>
                  )}
                </div>
              </div>

              {/* Contract Type and Experience */}
              <div className="flex items-center">
                <Briefcase className="feature-icon" />
                <div className="flex flex-col">
                  <span>{job.contract}</span>
                  {job.required_experience && (
                    <span className="text-sm text-text-light">
                      {job.required_experience}
                    </span>
                  )}
                </div>
              </div>

              {/* Salary Information */}
              {job.salary > 0 && (
                <div className="flex items-center">
                  <Euro className="feature-icon" />
                  <span>{formatSalary(job.salary)}/an</span>
                </div>
              )}
            </div>

            {/* Date of Posting */}
            <div className="mt-4 text-sm text-text-light">
              Publié le {formatDate(job.created_at)}
            </div>

            {/* View Details Button for Selected Job */}
            {selectedJobId === job.id && (
              <Link
                to={`/jobs/${job.id}`} // Navigate to the job details page
                className="cta-button mt-4 block w-full text-center"
              >
                Voir les détails
              </Link>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
